import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';

/** Servicio asociable a una factura (listado de "sin facturar" y refacturación). */
export interface BillableServiceDto {
    id: number;
    folio: number | string | null;
    origin_address: string | null;
    destination_address: string | null;
    upload_date: string | null;
}

/** Precarga de la pantalla de nueva factura en modo refacturación. */
export interface RefacturaDraft {
    original: {
        id: number;
        folio: string;
        uuid_sat: string | null;
        serie: string | null;
        issued_at: string | null;
        total: number;
        status: string;
    };
    billing_entity_id: number | null;
    client: {
        id: number | null;
        rfc: string | null;
        business_name: string | null;
        postal_code: string | null;
    };
    payment_form_key: string | null;
    payment_method_key: string | null;
    services: BillableServiceDto[];
    items: Array<{
        service_id: number | null;
        folio: number | string | null;
        route: string;
        service_description: string;
        quantity: number;
        unit_key: string;
        unit_price: number;
        product_service_key: string;
        tax_object_key: string;
    }>;
    /** false cuando el CFDI original no se pudo leer y el borrador es aproximado. */
    from_xml: boolean;
    /** true cuando lo que se devuelve es un borrador ya guardado, no el CFDI original. */
    has_draft: boolean;
    draft_saved_at: string | null;
}

/** Cuerpo para guardar el borrador o timbrar el sustituto. */
export interface RefacturaPayload {
    billing_entity_id: number;
    payment_form_key: string;
    payment_method_key: string;
    items: Array<{
        service_id: number;
        service_description: string;
        quantity: number;
        unit_key: string;
        unit_price: number;
        product_service_key: string;
        tax_object_key: string;
    }>;
}

/** Resultado del timbrado del CFDI sustituto. */
export interface RefacturaResult {
    invoice: any;
    uuid: string | null;
    service_ids: number[];
    pdf_url: string | null;
    xml_url: string | null;
    original: any;
    cancellation: {
        ok: boolean;
        sat: boolean;
        confirmed: boolean;
        sat_code: string | null;
        error: string | null;
    };
    relation: { type: string; description: string };
}

@Injectable({ providedIn: 'root' })
export class InvoiceService {
    private api_url = environment.url;

    constructor(private http: HttpClient) {}

    list(filters: {
        status?: string;
        payment_status?: string;
        payment_method?: string;
        client_id?: number;
        folio?: string;
        search?: string;
        issued_at?: string;
    } = {}) {
        const qs = Object.entries(filters)
            .filter(([, v]) => v !== undefined && v !== null && v !== '')
            .map(([k, v]) => `${k}=${encodeURIComponent(String(v))}`)
            .join('&');
        const url = `${this.api_url}invoice/list${qs ? `?${qs}` : ''}`;
        return this.http.get<any[]>(url);
    }

    accountsReceivable(creditDays: number = 30) {
        const url = `${this.api_url}invoice/accounts-receivable?credit_days=${creditDays}`;
        return this.http.get<{
            totals: {
                current: number;
                d1_30: number;
                d31_60: number;
                d61_90: number;
                d91_120: number;
                d120_plus: number;
                total: number;
            };
            rows: Array<{
                client_id: number;
                client_name: string;
                current: number;
                d1_30: number;
                d31_60: number;
                d61_90: number;
                d91_120: number;
                d120_plus: number;
                total: number;
            }>;
        }>(url);
    }

    /** Saldo de timbres SW + nivel de semáforo (green/yellow/red). */
    stampBalance(fresh = false) {
        const url = `${this.api_url}invoice/stamp-balance${fresh ? '?fresh=1' : ''}`;
        return this.http.get<{
            stamps_balance: number;
            stamps_used: number;
            stamps_assigned: number;
            is_unlimited: boolean;
            expiration_date: string | null;
            level: 'green' | 'yellow' | 'red';
            warning_threshold: number;
            critical_threshold: number;
        }>(url);
    }

    monthlyStats(month?: string) {
        const url = `${this.api_url}invoice/monthly-stats${month ? `?month=${encodeURIComponent(month)}` : ''}`;
        return this.http.get<{
            month: string;
            total_amount: number;
            issued_count: number;
            pending_amount: number;
            paid_count: number;
        }>(url);
    }

    byClient(clientId: number) {
        return this.http.get<any[]>(`${this.api_url}invoice/by-client/${clientId}`);
    }

    show(folio: string) {
        return this.http.get<any>(`${this.api_url}invoice/show/${folio}`);
    }

    generateForService(payload: { service_id: number; total: number }) {
        return this.http.post<any>(`${this.api_url}invoice/generate-for-service`, payload);
    }

    issueCfdi(payload: {
        service_id: number;
        billing_entity_id: number;
        payment_form_key: string;
        payment_method_key: string;
        service_description: string;
        quantity: number;
        unit_key: string;
        unit_price: number;
        product_service_key: string;
        tax_object_key: string;
    }) {
        return this.http.post<{
            invoice: any;
            uuid: string;
            pdf_url: string | null;
            xml_url: string;
        }>(`${this.api_url}invoice/issue`, payload);
    }

    /** Servicios de un cliente que aún no tienen factura (para consolidar). */
    billableServices(clientId: number) {
        return this.http.get<BillableServiceDto[]>(`${this.api_url}invoice/billable-services/${clientId}`);
    }

    /**
     * Datos del CFDI original para precargar la pantalla de refacturación.
     * Los conceptos se leen del XML timbrado, no de la BD.
     */
    refacturarDraft(folio: string) {
        return this.http.get<RefacturaDraft>(`${this.api_url}invoice/refacturar-draft/${folio}`);
    }

    /** Guarda la factura corregida sin timbrar nada. */
    saveRefacturaDraft(folio: string, payload: RefacturaPayload) {
        return this.http.post<{ draft: any }>(`${this.api_url}invoice/refacturar-draft/${folio}`, payload);
    }

    /** Timbra el CFDI sustituto y cancela la factura original (motivo 01). */
    refacturar(folio: string, payload: RefacturaPayload) {
        return this.http.post<RefacturaResult>(`${this.api_url}invoice/refacturar/${folio}`, payload);
    }

    /** Timbra varios servicios del mismo cliente en una sola factura. */
    issueConsolidated(payload: {
        billing_entity_id: number;
        payment_form_key: string;
        payment_method_key: string;
        items: Array<{
            service_id: number;
            service_description: string;
            quantity: number;
            unit_key: string;
            unit_price: number;
            product_service_key: string;
            tax_object_key: string;
        }>;
    }) {
        return this.http.post<{
            invoice: any;
            uuid: string;
            service_ids: number[];
            pdf_url: string | null;
            xml_url: string;
        }>(`${this.api_url}invoice/issue-consolidated`, payload);
    }

    cancel(folio: string, payload: { motive: string; uuid_replace?: string; note?: string }) {
        return this.http.post<{
            ok: boolean;
            sat: boolean;
            confirmed?: boolean;
            sat_code?: string | null;
            invoice: any;
            response?: any;
        }>(`${this.api_url}invoice/cancel/${folio}`, payload);
    }

    downloadXml(invoiceId: number) {
        return this.http.get(`${this.api_url}invoice/${invoiceId}/xml`, {
            responseType: 'blob',
            observe: 'response',
        });
    }

    downloadPdf(invoiceId: number) {
        return this.http.get(`${this.api_url}invoice/${invoiceId}/pdf`, {
            responseType: 'blob',
            observe: 'response',
        });
    }
}
